import { DateTime } from 'luxon';
import { DocumentSearchDetailForm } from '../services/http/documents.api';
import Formatter from './formatters';
import { WarehouseStatusCode, wareHouseRecords } from './warehouse.utils';

interface SearchConditionDescriptionOptions {
  maxLength?: number;
  documentTypeName?: string;
  folderName?: string;
}

export default class SearchConditionUtils {
  private static formatDateRange(from: DateTime | undefined, to: DateTime | undefined): string | null {
    if (!from && !to) return null;
    const fromStr = from ? Formatter.fromDateTimeToString(from, 'yyyy/MM/dd') : '';
    const toStr = to ? Formatter.fromDateTimeToString(to, 'yyyy/MM/dd') : '';
    if (fromStr && toStr) {
      return `${fromStr}〜${toStr}`;
    }
    if (fromStr) {
      return `${fromStr}以降`;
    }
    return `${toStr}以前`;
  }

  private static getWarehouseStatusLabel(status: WarehouseStatusCode): string | null {
    return wareHouseRecords[status] ?? null;
  }

  private static getElectronicContractLabel(value: 1 | 0 | undefined): string | null {
    if (value === 1) return '電子契約';
    if (value === 0) return '書面契約';
    return null;
  }

  private static getExistPaperLabel(value: 1 | 0 | undefined): string | null {
    if (value === 1) return '有り';
    if (value === 0) return '無し';
    return null;
  }

  private static getIncludeLabel(value: number | undefined): string | null {
    if (value === 1) return '有り';
    if (value === 0) return '無し';
    return null;
  }

  static buildConditions(
    form: DocumentSearchDetailForm | null,
    options: SearchConditionDescriptionOptions = {},
  ): string[] {
    if (!form) return [];

    const { documentTypeName, folderName } = options;
    const conditions: string[] = [];

    // 文書ID
    if (form.ccControlNumber) {
      conditions.push(`文書IDが「${form.ccControlNumber}」と一致`);
    }

    // 検索文字列 / 全文検索
    if (form.simpleString) {
      conditions.push(`キーワードに「${form.simpleString}」を含む`);
    }
    if (form.pdfSimpleString) {
      conditions.push(`全文に「${form.pdfSimpleString}」を含む`);
    }

    // 文書名
    if (form.name) {
      conditions.push(`文書名に「${form.name}」を含む`);
    }

    // PDFファイル名
    if (form.fileName) {
      conditions.push(`PDFファイル名に「${form.fileName}」を含む`);
    }

    // 文書種類
    if (form.documentTypeId && documentTypeName) {
      conditions.push(`文書種類が「${documentTypeName}」`);
    }

    // PDFファイル
    if (form.includePdf !== undefined) {
      const label = this.getIncludeLabel(form.includePdf);
      if (label) {
        conditions.push(`PDFファイルが「${label}」`);
      }
    }

    // 添付ファイル
    if (form.includeAttachment !== undefined) {
      const label = this.getIncludeLabel(form.includeAttachment);
      if (label) {
        conditions.push(`添付ファイルが「${label}」`);
      }
    }

    // 契約種別
    if (form.electronicContract !== undefined) {
      const label = this.getElectronicContractLabel(form.electronicContract);
      if (label) {
        conditions.push(`契約種別が「${label}」`);
      }
    }

    // 書面有無
    if (form.existPaper !== undefined) {
      const label = this.getExistPaperLabel(form.existPaper);
      if (label) {
        conditions.push(`書面有無が「${label}」`);
      }
    }

    // 登録日時
    const registDateRange = this.formatDateRange(form.registDate_From, form.registDate_To);
    if (registDateRange) {
      conditions.push(`登録日時が${registDateRange}`);
    }

    // 更新日時
    const updateDateRange = this.formatDateRange(form.updateDate_From, form.updateDate_To);
    if (updateDateRange) {
      conditions.push(`更新日時が${updateDateRange}`);
    }

    // メモ
    if (form.memo) {
      conditions.push(`メモに「${form.memo}」を含む`);
    }

    // ステータス
    if (form.warehouseStatus !== undefined) {
      const label = this.getWarehouseStatusLabel(form.warehouseStatus);
      if (label) {
        conditions.push(`ステータスが「${label}」`);
      }
    }

    // タグ
    if (form.tagList && form.tagList.length > 0) {
      form.tagList.forEach((tag) => {
        if (!tag.label) return;
        let tagCondition = '';
        if (tag.condition === '次の文字を含む' && tag.value1) {
          tagCondition = `${tag.label}に「${tag.value1}」を含む`;
        } else if (tag.condition === '次の文字と一致' && tag.value1) {
          tagCondition = `${tag.label}が「${tag.value1}」と一致`;
        } else if (tag.condition === '次の値と一致' && tag.value1) {
          tagCondition = `${tag.label}が「${tag.value1}」と一致`;
        } else if (tag.condition === '次の範囲' && (tag.value1 || tag.value2)) {
          const v1 = tag.value1 || '';
          const v2 = tag.value2 || '';
          tagCondition = `${tag.label}が${v1}〜${v2}`;
        } else if (tag.condition === '次の期間' && (tag.value1 || tag.value2)) {
          const v1 = tag.value1 ? Formatter.fromStringToFormattedString(tag.value1, 'yyyy/MM/dd') : '';
          const v2 = tag.value2 ? Formatter.fromStringToFormattedString(tag.value2, 'yyyy/MM/dd') : '';
          tagCondition = `${tag.label}が${v1}〜${v2}`;
        } else if (tag.condition === 'ブランク') {
          tagCondition = `${tag.label}がブランク`;
        }
        if (tagCondition) {
          conditions.push(tagCondition);
        }
      });
    }

    // 検索範囲
    if (form.searchRange && folderName !== undefined) {
      conditions.push(`検索範囲が「${folderName}」`);
    }

    return conditions;
  }

  static buildDescription(
    form: DocumentSearchDetailForm | null,
    options: SearchConditionDescriptionOptions = {},
  ): string {
    const { maxLength = 50 } = options;
    const conditions = this.buildConditions(form, options);

    if (conditions.length === 0) return '指定なし';

    const result = conditions.join(' かつ ');

    if (result.length > maxLength) {
      return `${result.substring(0, maxLength)}...`;
    }

    return result;
  }

  static buildTooltip(
    form: DocumentSearchDetailForm | null,
    options: SearchConditionDescriptionOptions = {},
  ): string {
    const conditions = this.buildConditions(form, options);

    if (conditions.length === 0) return '指定なし';

    return conditions.join('\n');
  }
}
